import { z } from "zod";
import { userDtoSchema } from "./user.js";
import { membershipRoleSchema } from "./membership.js";

/**
 * A membership as seen from the org's members page — the user inline (the
 * table needs an email, not a second round trip), plus `isOwner` computed
 * server-side. The UI locks a row on `isOwner`, not on `role === "owner"`,
 * so "the first admin can't be removed" survives however the role reads.
 */
export const orgMemberDtoSchema = z.object({
  user: userDtoSchema,
  role: membershipRoleSchema,
  isOwner: z.boolean(),
  createdAt: z.string().datetime(),
});

export type OrgMemberDto = z.infer<typeof orgMemberDtoSchema>;

/** Only admin/agent are assignable from the panel — "owner" is set once, at org creation, never chosen. */
export const assignableMembershipRoleSchema = z.enum(["admin", "agent"]);
export type AssignableMembershipRole = z.infer<typeof assignableMembershipRoleSchema>;

export const addMemberBodySchema = z.object({
  email: z.string().email(),
  role: assignableMembershipRoleSchema,
});
export type AddMemberBody = z.infer<typeof addMemberBodySchema>;

export const updateMemberRoleBodySchema = z.object({
  role: assignableMembershipRoleSchema,
});
export type UpdateMemberRoleBody = z.infer<typeof updateMemberRoleBodySchema>;

export const createOrganizationBodySchema = z.object({
  name: z.string().min(1).max(120),
  slug: z
    .string()
    .min(1)
    .max(60)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, "lowercase letters, numbers and hyphens only"),
  adminEmail: z.string().email(),
});
export type CreateOrganizationBody = z.infer<typeof createOrganizationBodySchema>;

/** Organization list row for the agency UI — the org plus how many people are in it. */
export const agencyOrganizationDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  memberCount: z.number().int().nonnegative(),
  createdAt: z.string().datetime(),
});
export type AgencyOrganizationDto = z.infer<typeof agencyOrganizationDtoSchema>;
